/**
 * Layout de l'espace d'administration.
 *
 * TROIS entrées courantes, et rien d'autre. Elles suivent le parcours d'une
 * personne administrative qui reçoit un nouveau programme :
 *
 *   Aujourd'hui  — qu'est-ce qu'on attend de moi ? On entre par là.
 *   Le dossier   — où en est-on, et que fais-je maintenant ?
 *   Mise en ligne— qu'est-ce qui va changer pour les élèves ?
 *
 * « Le dossier » et « Mise en ligne » n'apparaissent que lorsqu'un dossier est
 * ouvert : proposer une entrée qui mène à une page vide, c'est faire chercher
 * ce qui n'existe pas.
 *
 * CONSULTER, replié — les trois écrans qu'une personne administrative ouvre
 * d'elle-même : le catalogue tel que l'élève le voit, la fabrique d'exercices,
 * et ce que les utilisateurs signalent.
 *
 * ÉCRANS TECHNIQUES — rendus en petits liens, pas en entrées de menu, parce
 * qu'ils ne s'adressent pas à l'administration. Ce qu'ils affichent —
 * rapprochements à qualifier, couverture par attendu, générateurs certifiés
 * sans chapitre — se règle avec l'outil de développement, pas à la main.
 * Rien n'est supprimé : leurs URL répondent toutes.
 *
 * Ce qui a changé le 07/09/2026 : l'administration n'entre plus par un
 * inventaire. Les cinq entrées précédentes — À traiter, Programme, Chapitres,
 * Atelier, Retours — décrivaient des OUTILS ; les nouvelles décrivent un
 * PARCOURS. Une personne qui découvre l'outil ne sait pas ce qu'est un
 * chapitre ; elle sait qu'elle a reçu un programme.
 *
 * Ce qui a changé le 09/09/2026 : neuf entrées « Avancé » en faisaient trois
 * portes concurrentes. « Mises en page » et « Référentiel » quittent le menu —
 * la première ne charge plus (les gabarits d'affichage datent d'avant le
 * contrat actuel des générateurs), la seconde est vide depuis que ses
 * écritures répondent 410.
 */
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useContexteAdmin } from "../../hooks/useContexteAdmin";
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileText,
  MessageSquare,
  Settings,
  Sparkles,
} from "lucide-react";

//: Les trois écrans du parcours courant. `dossier` signale les entrées qui
//: n'ont de sens qu'avec un dossier ouvert.
const SECTIONS = [
  { path: "/admin/aujourdhui", label: "Aujourd'hui", icon: ClipboardList },
  { path: "/admin/prototype", label: "Prototype Admin", icon: Sparkles },
  { path: "/admin/dossier", label: "Le dossier", icon: FileText, dossier: true },
];

//: Replié par défaut. Les trois écrans qu'une personne administrative ouvre
//: d'elle-même, nommés par ce qu'on y voit — pas par la table qu'ils éditent.
const CONSULTER = [
  { path: "/admin/chapitres", label: "Chapitres", icon: BookOpen,
    hint: "le catalogue tel que l'utilisateur le voit" },
  { path: "/admin/atelier", label: "Atelier", icon: Sparkles, local: true,
    hint: "fabriquer une série d'exercices — poste de développement" },
  { path: "/admin/retours", label: "Retours", icon: MessageSquare,
    hint: "les signalements des utilisateurs" },
];

//: Ces écrans ne s'adressent pas à l'administration : ce qu'ils listent se
//: règle avec l'outil de développement. On les garde atteignables — un lien
//: discret, jamais une entrée de menu — parce que les masquer entièrement
//: ferait chercher une capacité qui existe encore.
const TECHNIQUES = [
  { path: "/admin/programme", label: "Programme" },
  { path: "/admin/pilotage", label: "Pilotage" },
  { path: "/admin/a-traiter", label: "À traiter" },
  { path: "/admin/prets-non-en-ligne", label: "Prêts non en ligne" },
  { path: "/admin/exercises-archive", label: "Archives" },
];

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { atelier_disponible, connu } = useContexteAdmin();
  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  // Ouvert d'office si l'on s'y trouve déjà : on ne cache jamais où l'on est.
  const [advancedOpen, setAdvancedOpen] = useState(() =>
    CONSULTER.some((s) => isActive(s.path))
  );
  const [techniquesOuvert, setTechniquesOuvert] = useState(() =>
    TECHNIQUES.some((s) => isActive(s.path))
  );

  // « Le dossier » n'a de sens qu'avec un dossier ouvert. Proposer une entrée
  // qui mène à une page vide ferait chercher ce qui n'existe pas.
  const dansUnDossier = location.pathname.startsWith("/admin/dossier/");
  const visibles = SECTIONS.filter((s) => !s.dossier || dansUnDossier).map((s) =>
    s.dossier ? { ...s, path: location.pathname.split("/").slice(0, 4).join("/") } : s
  );

  const itemClass = (path) =>
    `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-left transition-colors ${
      isActive(path)
        ? "bg-gray-900 text-white"
        : "text-gray-700 hover:bg-gray-200"
    }`;

  return (
    <div className="flex min-h-[calc(100vh-60px)]">
      <aside className="hidden md:flex w-56 shrink-0 flex-col border-r border-gray-200 bg-gray-50 px-3 py-5">
        <div className="flex items-center gap-2 px-2 mb-5 text-gray-900 font-semibold">
          <Settings className="h-5 w-5 text-green-600" />
          Administration
        </div>

        <nav className="flex flex-col gap-1">
          {visibles.map(({ path, label, icon: Icon, local }) => {
            // Indisponible ici : on l'écrit, on ne le cache pas. Le lien reste
            // cliquable et mène à un écran qui explique où fabriquer.
            const ailleurs = local && connu && !atelier_disponible;
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={itemClass(path)}
                title={ailleurs ? "Se fait sur le poste de développement" : undefined}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate text-left">{label}</span>
                {ailleurs && (
                  <span className={`shrink-0 rounded px-1 text-[10px] font-medium ${
                    isActive(path)
                      ? "bg-white/20 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}>
                    local
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="mt-5 border-t border-gray-200 pt-3">
          <button
            onClick={() => setAdvancedOpen(!advancedOpen)}
            className="flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            {advancedOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            Consulter
          </button>
          {advancedOpen && (
            <nav className="mt-1 flex flex-col gap-1">
              {CONSULTER.map(({ path, label, icon: Icon, hint }) => (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className={itemClass(path)}
                  title={hint}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 truncate">{label}</span>
                </button>
              ))}
            </nav>
          )}
        </div>

        {/* Des liens, pas des entrées de menu : ces écrans ne s'adressent pas
            à l'administration, et leur donner la même forme que « Chapitres »
            les ferait passer pour du travail à faire. */}
        <div className="mt-auto pt-6">
          <button
            onClick={() => setTechniquesOuvert(!techniquesOuvert)}
            className="flex w-full items-center gap-1.5 px-3 py-1.5 text-[11px]
                       text-gray-400 hover:text-gray-600"
          >
            {techniquesOuvert ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            Écrans techniques
          </button>
          {techniquesOuvert && (
            <div className="px-3 pt-1">
              <p className="mb-1.5 text-[11px] leading-snug text-gray-400">
                Pour le développeur. Ce qui y est listé se règle avec l'outil de
                développement, pas à la main.
              </p>
              <div className="flex flex-wrap gap-x-2.5 gap-y-1">
                {TECHNIQUES.map(({ path, label }) => (
                  <button
                    key={path}
                    onClick={() => navigate(path)}
                    className={`text-[11px] underline-offset-2 hover:underline ${
                      isActive(path) ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Barre mobile : seulement l'usage courant, l'avancé se rejoint par URL.
          `pb-safe` et une hauteur de cible d'au moins 44 px : cette barre est
          fixée au bord bas, là où le pouce se pose. */}
      {/* Une barre à une seule entrée occupe le bas de l'écran pour rien :
          hors dossier, il n'y a qu'« Aujourd'hui », et on y est déjà. */}
      <div className={`${visibles.length > 1 ? "md:hidden flex" : "hidden"}
                      fixed bottom-0 inset-x-0 z-40 border-t border-gray-200
                      bg-white px-1.5 pb-[env(safe-area-inset-bottom)] gap-1
                      overflow-x-auto`}>
        {visibles.map(({ path, label, icon: Icon }) => (
          <button
            key={path}
            onClick={() => navigate(path)}
            className={`flex min-h-[48px] shrink-0 flex-col items-center justify-center
                        gap-0.5 rounded px-2.5 py-1.5 text-[11px] leading-none ${
              isActive(path) ? "bg-gray-900 text-white" : "text-gray-600"
            }`}
          >
            <Icon className="h-4 w-4" />
            <span className="whitespace-nowrap">{label}</span>
          </button>
        ))}
      </div>

      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
};

export default AdminLayout;
